import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import LoadingBox from "common/components/LoadingBox";
import * as api from "core-module/services/api";
import config from "config.js";
import withStyles from "@material-ui/core/styles/withStyles";

const styles = {
  page: {
    width: "100%",
    maxWidth: "820px",
    margin: "12px auto 40px",
    color: "#123e68",
  },
  backLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "24px",
    color: "#176b99",
    fontSize: "14px",
    fontWeight: 500,
    "& svg": { fontSize: "18px" },
    "&:hover": { color: "#123e68", textDecoration: "underline" },
    "&:focus-visible": { outline: "2px solid #176b99", outlineOffset: "4px" },
  },
  title: {
    margin: "0 0 8px",
    fontSize: "30px",
    lineHeight: 1.25,
    fontWeight: 600,
    letterSpacing: "-0.5px",
  },
  intro: {
    margin: "0 0 28px",
    color: "#5f6f7e",
    fontSize: "16px",
    lineHeight: 1.6,
  },
  card: {
    background: "#fff",
    border: "1px solid #dce5eb",
    borderRadius: "12px",
    boxShadow: "0 8px 28px rgba(20, 62, 103, 0.06)",
    overflow: "hidden",
  },
  section: {
    padding: "30px 32px",
    "& + &": { borderTop: "1px solid #e7edf1" },
    "@media (max-width: 600px)": { padding: "24px 20px" },
  },
  sectionHeading: {
    display: "flex",
    alignItems: "flex-start",
    gap: "14px",
    marginBottom: "26px",
  },
  icon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "44px",
    height: "44px",
    borderRadius: "10px",
    background: "#eaf6fb",
    color: "#177ba5",
  },
  sectionTitle: {
    margin: "0 0 6px",
    fontSize: "19px",
    lineHeight: 1.4,
    fontWeight: 600,
  },
  description: {
    margin: 0,
    color: "#5f6f7e",
    fontSize: "14px",
    lineHeight: 1.6,
  },
  fields: {
    display: "grid",
    gap: "22px",
    "& .MuiFormControl-root": { minWidth: 0 },
    "& .MuiOutlinedInput-root": { borderRadius: "7px", color: "#263e50" },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#bdcbd5" },
    "& .MuiInputLabel-root": { color: "#526879" },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#177ba5",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: "#177ba5" },
    "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: "#b33a3a",
    },
    "& .MuiInputLabel-root.Mui-error": { color: "#b33a3a" },
    "& .MuiFormHelperText-root": { margin: "7px 2px 0", lineHeight: 1.5 },
  },
  locality: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)",
    gap: "20px",
    "@media (max-width: 700px)": {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      "& > :first-child": { gridColumn: "1 / -1" },
    },
  },
  postalCode: {
    "& input": { textTransform: "uppercase", letterSpacing: "0.6px" },
  },
  inlineError: {
    margin: "24px 32px 0",
    padding: "12px 14px",
    borderRadius: "7px",
    border: "1px solid #f3c2c2",
    background: "#fff5f5",
    color: "#a52b2b",
    fontSize: "14px",
    "@media (max-width: 600px)": { margin: "20px 20px 0" },
  },
  loadError: {
    padding: "24px 32px",
    "& $inlineError": { margin: "0 0 16px" },
    "@media (max-width: 600px)": { padding: "20px" },
  },
  actions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "20px",
    padding: "22px 32px",
    borderTop: "1px solid #e7edf1",
    background: "#f8fafc",
    "@media (max-width: 600px)": { padding: "20px" },
  },
  requiredNote: { color: "#5f6f7e", fontSize: "12px", margin: 0 },
  primaryButton: {
    "&&": {
      minHeight: "44px",
      padding: "10px 22px",
      borderRadius: "7px",
      backgroundColor: "#177ba5",
      color: "#fff",
      fontSize: "14px",
      fontWeight: 600,
      textTransform: "none",
      boxShadow: "none",
      "&:hover": { backgroundColor: "#12658b", boxShadow: "none" },
      "&.Mui-disabled": { backgroundColor: "#dce5eb", color: "#526879" },
      "&.Mui-focusVisible": {
        outline: "2px solid #123e68",
        outlineOffset: "3px",
      },
    },
    "@media (max-width: 480px)": { width: "100%" },
  },
};

const canadianPostalCode = /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVWXYZ] \d[ABCEGHJKLMNPRSTVWXYZ]\d$/;

function formatPostalCode(value) {
  const characters = value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 6);
  return characters.length > 3
    ? characters.slice(0, 3) + " " + characters.slice(3)
    : characters;
}

const PreinstallAdditionalInfo = (props) => {
  const { classes } = props;
  const userid = useSelector((state) => state.services.ids.user);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [postalTouched, setPostalTouched] = useState(false);
  const postalInput = useRef(null);
  const postalCaret = useRef(null);
  const [advsrcoptions, setAdvsrcoptions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [form, setForm] = useState({
    mailingaddress: "",
    mailingaddress2: "",
    town: "",
    regionid: "",
    postalcode: "",
    advsrc: "",
    advsrctext: "",
  });

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setLoadError("");
    api
      .get("user." + userid + "/preinstalladditionalinfo")
      .then((response) => {
        if (!isMounted) {
          return;
        }
        api.requireApiSuccess(
          response,
          "Unable to load additional information."
        );
        const data = response?.object || response?.list?.[0] || response;
        setAdvsrcoptions(data?.advsrcoptions || []);
        setRegionOptions(
          response?.schema?.props?.regionid?.dropdownOptions || []
        );
        setForm({
          mailingaddress: data?.mailingaddress || "",
          mailingaddress2: data?.mailingaddress2 || "",
          town: data?.town || "",
          regionid: data?.regionid || "",
          postalcode: formatPostalCode(data?.postalcode || ""),
          advsrc: data?.advsrc || "",
          advsrctext: data?.advsrctext || "",
        });
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) {
          return;
        }
        setLoadError(e.message || "Unable to load additional information.");
        setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [userid, loadAttempt]);

  useLayoutEffect(() => {
    // Keep edits in the middle of the code from jumping to the end, even when
    // deleting the separator leaves the formatted value unchanged.
    if (postalCaret.current !== null && postalInput.current) {
      postalInput.current.setSelectionRange(
        postalCaret.current,
        postalCaret.current
      );
      postalCaret.current = null;
    }
  });

  const updateForm = (propName, value) => {
    setForm((current) => ({ ...current, [propName]: value }));
  };

  const updatePostalCode = (event) => {
    const { value, selectionStart } = event.target;
    const charactersBeforeCaret = value
      .slice(0, selectionStart === null ? value.length : selectionStart)
      .replace(/[^a-z0-9]/gi, "").length;
    const formatted = formatPostalCode(value);
    postalCaret.current = Math.min(
      charactersBeforeCaret + (charactersBeforeCaret > 3 ? 1 : 0),
      formatted.length
    );
    updateForm("postalcode", formatted);
  };

  const saveAdditionalInfo = async (event) => {
    event.preventDefault();
    if (saving) {
      return;
    }
    setPostalTouched(true);
    const requiredFields = [
      "mailingaddress",
      "town",
      "regionid",
      "postalcode",
      "advsrc",
    ];
    if (
      requiredFields.some((propName) => String(form[propName]).trim() === "")
    ) {
      setError("Please complete all required fields.");
      return;
    }
    if (!canadianPostalCode.test(form.postalcode)) {
      setError("Please enter a valid Canadian postal code.");
      postalInput.current.focus();
      return;
    }

    setSaving(true);
    setError("");
    try {
      api.requireApiSuccess(
        await api.post("user." + userid + "/preinstalladditionalinfo", {
          ...form,
          regionid: Number(form.regionid),
        }),
        "Unable to save additional information."
      );
      props.history.push(
        config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"
      );
    } catch (e) {
      setError(e.message || "Unable to save additional information.");
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingBox />;
  }

  const postalInvalid =
    postalTouched && !canadianPostalCode.test(form.postalcode);

  return (
    <div className={classes.page}>
      <Link
        className={classes.backLink}
        to={config.MYACCOUNT.APP_PATH + "/user/preinstallchecklist"}
      >
        <ArrowBackIcon /> Installation checklist
      </Link>
      <h1 className={classes.title}>Additional information</h1>
      <p className={classes.intro}>
        A few final details to help us set up your account.
      </p>
      <div className={classes.card}>
        {loadError ? (
          <div className={classes.loadError}>
            <p role="alert" className={classes.inlineError}>
              {loadError}
            </p>
            <Button onClick={() => setLoadAttempt((attempt) => attempt + 1)}>
              Try again
            </Button>
          </div>
        ) : (
          <form
            onSubmit={saveAdditionalInfo}
            noValidate
            aria-label="Additional information"
          >
            {error ? (
              <div role="alert" className={classes.inlineError}>
                {error}
              </div>
            ) : null}
            <section
              className={classes.section}
              aria-labelledby="mailing-heading"
            >
              <div className={classes.sectionHeading}>
                <span className={classes.icon}>
                  <MailOutlineIcon />
                </span>
                <div>
                  <h2 id="mailing-heading" className={classes.sectionTitle}>
                    Mailing address
                  </h2>
                  <p className={classes.description}>
                    Where you receive mail. A PO box is welcome.
                  </p>
                </div>
              </div>
              <div className={classes.fields}>
                <TextField
                  id="mailingaddress"
                  name="mailingaddress"
                  autoComplete="address-line1"
                  fullWidth
                  required
                  variant="outlined"
                  label="Mailing address"
                  placeholder="Street address or PO box"
                  disabled={saving}
                  value={form.mailingaddress}
                  onChange={(event) =>
                    updateForm("mailingaddress", event.target.value)
                  }
                />
                <TextField
                  id="mailingaddress2"
                  name="mailingaddress2"
                  autoComplete="address-line2"
                  fullWidth
                  variant="outlined"
                  label="Address line 2 (optional)"
                  disabled={saving}
                  value={form.mailingaddress2}
                  onChange={(event) =>
                    updateForm("mailingaddress2", event.target.value)
                  }
                />
                <div className={classes.locality}>
                  <TextField
                    id="town"
                    name="town"
                    autoComplete="address-level2"
                    fullWidth
                    required
                    variant="outlined"
                    label="Town or city"
                    disabled={saving}
                    value={form.town}
                    onChange={(event) => updateForm("town", event.target.value)}
                  />
                  <TextField
                    id="regionid"
                    name="regionid"
                    select
                    fullWidth
                    required
                    variant="outlined"
                    label="Province"
                    disabled={saving}
                    value={String(form.regionid)}
                    onChange={(event) =>
                      updateForm("regionid", event.target.value)
                    }
                  >
                    {regionOptions.map((option) => (
                      <MenuItem key={option[0]} value={String(option[0])}>
                        {option[1]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    id="postalcode"
                    name="postalcode"
                    autoComplete="postal-code"
                    fullWidth
                    required
                    variant="outlined"
                    label="Postal code"
                    placeholder="T0A 3A0"
                    className={classes.postalCode}
                    inputRef={postalInput}
                    inputProps={{
                      autoCapitalize: "characters",
                      spellCheck: false,
                    }}
                    disabled={saving}
                    error={postalInvalid}
                    helperText={
                      postalInvalid
                        ? "Use a postal code like T0A 3A0."
                        : "Example: T0A 3A0"
                    }
                    value={form.postalcode}
                    onChange={updatePostalCode}
                    onBlur={() => setPostalTouched(true)}
                  />
                </div>
              </div>
            </section>
            <section
              className={classes.section}
              aria-labelledby="heard-about-heading"
            >
              <div className={classes.sectionHeading}>
                <div>
                  <h2 id="heard-about-heading" className={classes.sectionTitle}>
                    How did you find us?
                  </h2>
                  <p className={classes.description}>
                    Let us know what brought you to MCSnet.
                  </p>
                </div>
              </div>
              <div className={classes.fields}>
                <TextField
                  id="advsrc"
                  name="advsrc"
                  select
                  fullWidth
                  required
                  variant="outlined"
                  label="Where did you hear about MCSnet?"
                  disabled={saving}
                  value={form.advsrc}
                  onChange={(event) => updateForm("advsrc", event.target.value)}
                >
                  {advsrcoptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                {form.advsrc === "Other (specify)" ? (
                  <TextField
                    id="advsrctext"
                    name="advsrctext"
                    fullWidth
                    variant="outlined"
                    label="Please specify"
                    disabled={saving}
                    value={form.advsrctext}
                    onChange={(event) =>
                      updateForm("advsrctext", event.target.value)
                    }
                  />
                ) : null}
              </div>
            </section>
            <div className={classes.actions}>
              <p className={classes.requiredNote}>* Required fields</p>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.primaryButton}
                disabled={saving}
                endIcon={<ArrowForwardIcon />}
              >
                {saving ? "Saving…" : "Save information"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default withStyles(styles)(PreinstallAdditionalInfo);
